import { Bell, ChevronDown, Menu, CircleUser } from 'lucide-react'; // 1. Import CircleUser
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useGetAgencyProfile } from '../../../service/hooks/useAgency';

// Mobile Sidebar Toggle Component
interface MobileSidebarToggleProps {
  onClick: () => void;
}

export function MobileSidebarToggle({ onClick }: MobileSidebarToggleProps) {
  return (
    <button
      onClick={onClick}
      className="min-991:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
      aria-label="Toggle mobile sidebar"
    >
      <Menu size={24} className="text-gray-700" />
    </button>
  );
}

// Header Component
interface HeaderProps {
  onMobileSidebarToggle?: () => void;
}

export default function Header({ onMobileSidebarToggle }: HeaderProps) {
  const user = useSelector((state: RootState) => state?.auth?.user);
  const { agencyDetails } = useGetAgencyProfile();

  // --- Date Logic ---
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const getGreeting = () => {
    const hour = currentDate.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.userName ? user.userName.split(' ')[0] : 'User';

  // 2. Extract URL safely
  const profileUrl = agencyDetails?.logo?.documentUrl;

  return (
    <header className="h-[76px] sticky z-40 top-0 left-0 right-0 border-b border-[#E5E7EB] px-4 min-991:px-6 bg-white">
      <div className="h-full flex items-center justify-between">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          {onMobileSidebarToggle && (
            <MobileSidebarToggle onClick={onMobileSidebarToggle} />
          )}

          <div className="hidden md:flex flex-col">
            <p className="text-lg font-semibold text-gray-900">
              {getGreeting()}, {firstName}
            </p>
            <p className="text-sm text-gray-600">Today is {formattedDate}</p>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              {/* 3. Conditional Rendering: Image vs Lucide Icon */}
              {profileUrl ? (
                <img
                  src={profileUrl}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover object-top border border-gray-200"
                  onError={(e) => {
                    // Fallback to hiding image and showing parent bg if broken
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                // Fallback Avatar Icon
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                  <CircleUser className="w-6 h-6 text-gray-400" />
                </div>
              )}

              <div className="hidden min-991:block">
                <p className="text-sm font-medium text-[#374151]">
                  {user?.userName || 'Guest User'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
